import { forkJoin, Observable, switchMap } from 'rxjs';
import { post } from '@/shared/api/http';
import type { NotificationPayload, GenerateResponse, LatLngPoint } from '@/shared/types';

const BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8000';
const TOKEN_KEY = 'auth_token';

function toGeoJsonPoint(p: LatLngPoint) {
  return { type: 'Point', coordinates: [p.lng, p.lat] };
}

function toGeoJsonPolygon(points: LatLngPoint[]) {
  const coords = points.map(p => [p.lng, p.lat]);
  coords.push(coords[0]); // close the ring
  return { type: 'Polygon', coordinates: [coords] };
}

function toGeoJsonMultiPoint(points: LatLngPoint[]) {
  return { type: 'MultiPoint', coordinates: points.map(p => [p.lng, p.lat]) };
}

function mapToBackendPayload(payload: NotificationPayload): Record<string, unknown> {
  const body: Record<string, unknown> = {
    device: payload.deviceId,
    operator: payload.operatorId,
    purpose: payload.purpose,
    flight_date: payload.flightDate,
    time_from: payload.flightTimeFrom,
    time_to: payload.flightTimeTo,
    location_name: payload.location,
    takeoff_point: toGeoJsonPoint(payload.takeoffPoint),
    planned_altitude_m: payload.plannedAltitude,
    max_distance_m: payload.maxDistance ?? 0,
    category: payload.category,
    buffer_zone_meters: payload.bufferZoneMeters,
    termination_zone_meters: payload.terminationZoneMeters,
  };
  if (payload.flightArea && payload.flightArea.length >= 3) {
    body.flight_area = toGeoJsonPolygon(payload.flightArea);
  }
  if (payload.additionalTakeoffPoints && payload.additionalTakeoffPoints.length > 0) {
    body.additional_takeoff_points = toGeoJsonMultiPoint(payload.additionalTakeoffPoints);
  }
  return body;
}

function fetchBlobUrl(path: string): Observable<string> {
  return new Observable(subscriber => {
    const token = localStorage.getItem(TOKEN_KEY);
    fetch(`${BASE_URL}${path}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.blob();
      })
      .then(blob => {
        subscriber.next(URL.createObjectURL(blob));
        subscriber.complete();
      })
      .catch(err => subscriber.error(err));
  });
}

function createNotification(payload: NotificationPayload): Observable<{ id: number }> {
  return post<{ id: number }>('/api/v1/flights/notifications/', mapToBackendPayload(payload));
}

export function submitNotification(payload: NotificationPayload): Observable<GenerateResponse> {
  return createNotification(payload).pipe(
    switchMap(({ id }) =>
      post<{ detail: string }>(`/api/v1/flights/notifications/${id}/submit/`, {}).pipe(
        switchMap(() =>
          forkJoin({
            pdfUrl: fetchBlobUrl(`/api/v1/flights/notifications/${id}/download-pdf/`),
            kmlUrl: fetchBlobUrl(`/api/v1/flights/notifications/${id}/download-kml/`),
          })
        )
      )
    )
  );
}

// Generates the PDF/KML for a new draft notification without submitting it —
// no status change, no email sent to the CAA.
export function generateNotification(payload: NotificationPayload): Observable<GenerateResponse> {
  return createNotification(payload).pipe(
    switchMap(({ id }) =>
      forkJoin({
        pdfUrl: fetchBlobUrl(`/api/v1/flights/notifications/${id}/generate-pdf/`),
        kmlUrl: fetchBlobUrl(`/api/v1/flights/notifications/${id}/generate-kml/`),
      })
    )
  );
}
